'use client';

import {Button as RMWCButton} from '@rmwc/button';
import React, {type ReactNode} from 'react';
import {useCallback, useRef} from 'react';
import type {MouseEventHandler, PropsWithChildren} from 'react';
import styles from './Button.module.scss';
import '@rmwc/circular-progress/styles';
import '@rmwc/button/styles';
import type {ButtonProps} from '@rmwc/button';
import {CircularProgress} from '@rmwc/circular-progress';
import {removeFocus} from '../../app/utils';
import {type ButtonAction, ButtonActionType, ButtonType} from './Button.types';

export interface PropsTypes extends ButtonProps, PropsWithChildren {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  round?: boolean;
  loading?: boolean;
  widget: {
    title?: string | ReactNode;
    buttonType?: ButtonType;
    action?: ButtonAction;
  };
  className?: string;
}

/**
 * get classes based on the ButtonType
 */
function getClasses({
  widget: {buttonType},
  round,
  loading,
  className,
  disabled,
}: PropsTypes): string {
  const base = [styles.button];
  switch (buttonType) {
    case ButtonType.SECONDARY:
      base.push(styles.secondaryType);
      break;
    case ButtonType.TEXT:
      base.push(styles.textType);
      break;
    case ButtonType.TERTIARY:
      base.push(styles.tertiaryType);
      break;
    default:
      // default for case ButtonType.PRIMARY
      base.push(styles.primaryType);
  }
  if (round) {
    base.push(styles.round);
  }
  if (disabled) {
    base.push(styles.disabled);
  }
  if (loading) {
    base.push(styles.loading);
  }
  if (className) {
    base.push(className);
  }
  return base.join(' ');
}

/** Return the Type To change button behaviour as submit|reset|buttton */
const getButtonType = (action?: ButtonAction) => {
  switch (action) {
    case ButtonActionType.SUBMIT:
      return 'submit';
    case ButtonActionType.CANCEL:
      return 'reset';
    default:
      return 'button';
  }
};

/**
 * get variant class based on the ButtonType
 */
function getVariant(buttonType?: ButtonType): string {
  switch (buttonType) {
    case ButtonType.SECONDARY:
      return 'outlined';
    case ButtonType.TEXT:
      return 'dense';
    case ButtonType.TERTIARY:
      return 'outlined';
    default:
      // default for case ButtonType.PRIMARY
      return 'raised';
  }
}

/**
 * reused Button component
 */
const Button = (props: PropsTypes) => {
  const {
    children,
    widget: {title, buttonType, action},
    //round, // cannot be passed to RMWCButton
    loading,
    icon,
    onClick,
    onKeyDown,
    ...buttonProps
  } = props;

  const ele = useRef<HTMLElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      removeFocus(ele, onClick, e);
    },
    [ele, onClick],
  );

  return (
    <span ref={ele}>
      <RMWCButton
        type={getButtonType(action)}
        {...buttonProps}
        {...{
          onClick: handleClick,
          onKeyDown: onKeyDown,
          className: getClasses(props),
          label: title,
          [getVariant(buttonType)]: true,
          icon:
            icon ||
            (loading && (
              <CircularProgress
                label="xsmall progress"
                size="xsmall"
              />
            )),
        }}
      >
        {children || ''}
      </RMWCButton>
    </span>
  );
};

export {Button};
